import React from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";

const Step = ({ n, title, when, body, testid }) => (
  <div className="grid md:grid-cols-12 gap-8 py-12 border-t border-line" data-testid={testid}>
    <div className="md:col-span-2 font-mono text-sm text-oxblood">{n}</div>
    <div className="md:col-span-10">
      <div className="overline">{when}</div>
      <h3 className="h-serif text-3xl md:text-4xl text-ink mt-2">{title}</h3>
      <p className="text-sub leading-[1.85] mt-4 max-w-3xl">{body}</p>
    </div>
  </div>
);

export default function Process() {
  return (
    <div className="bg-parchment min-h-screen" data-testid="process-page">
      <PublicNav />

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-12">
        <div className="overline">The deal-flow pipeline — six irrevocable steps</div>
        <h1 className="h-serif text-5xl md:text-7xl text-ink mt-4 leading-[1.0] max-w-5xl">
          How the deal actually closes.
        </h1>
        <p className="text-sub mt-6 max-w-2xl leading-relaxed">
          We never hold inventory. The platform automates a deal-flow intake system that
          binds the buyer, the seller, and any co-brokers to a legal hierarchy long
          before any commercial terms are exchanged. Every counterparty clears KYC,
          sanctions and background vetting before a single specification is exchanged —
          this is published openly so there are no surprises later.
        </p>
      </section>

      {/* THE PIPELINE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-12">
        <div className="overline">The pipeline</div>
        <h2 className="h-serif text-3xl md:text-4xl text-ink mt-2">
          KYC → ICPO → Soft Probe → FCO → IMFPA → SPA → Commission Clears
        </h2>
        <Step n="STEP 01" when="Compliance gate — mandatory, runs first"
              title="KYC, sanctions screening &amp; background vetting"
              body="Before any commercial conversation happens, every prospective buyer completes our corporate KYC profile — legal entity name, jurisdiction, registration number, beneficial owners, countries of operation, PEP disclosure and three signed attestations. The brokerage desk then runs the entity and every beneficial owner against OFAC SDN, the EU, UN and UK consolidated sanctions lists and global PEP databases through OpenSanctions. Buyers cannot submit an ICPO until the desk marks the file 'cleared'. This step is non-negotiable and is enforced in code, not just policy."
              testid="process-step-01" />
        <Step n="STEP 02" when="Secure Intake — buyer dashboard"
              title="The buyer submits an ICPO + Bank Comfort Letter"
              body="Once cleared, the buyer logs in and submits an Irrevocable Corporate Purchase Order (ICPO) alongside a Bank Comfort Letter (BCL) from a top-tier institution. The BCL is the irrevocable proof of funds — it tells us this is a buyer that can actually move the volume requested in sugar, coffee or crude."
              testid="process-step-02" />
        <Step n="STEP 03" when="Sanitized RFQ — automated"
              title="The platform strips the buyer's identity"
              body="Our system removes the buyer's name, logo and contact details from the request before it reaches any supplier. This protects our trade secrets and ensures the only path between buyer and seller runs through our desk."
              testid="process-step-03" />
        <Step n="STEP 04" when="Seller Match — broker network"
              title="A vetted seller issues an FCO"
              body="We present the sanitized request to our vetted network of refineries, mills and coffee origins. The seller responds with a Full Corporate Offer detailing pricing, origin, and allocation capacity."
              testid="process-step-04" />
        <Step n="STEP 05" when="Introduction &amp; Lock-In — sealed"
              title="Legal protections lock in before any handshake"
              body="Before the buyer and seller are ever introduced, our automated platform lodges the legal documents that ensure we are paid. The sequence below is not optional — it is the entire product."
              testid="process-step-05" />
        <Step n="STEP 06" when="Execution — bank-routed payout"
              title="SPA executes; commission clears off the top"
              body="The buyer's bank funds the seller's bank through a Documentary or Standby Letter of Credit. The IMFPA cuts our brokerage fee out of the transfer automatically and routes it directly to our LLC's account before the seller receives their main payout."
              testid="process-step-06" />
      </section>

      {/* LEGAL HIERARCHY */}
      <section className="bg-parchment2 border-y border-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="overline">The legal hierarchy</div>
          <h2 className="h-serif text-4xl md:text-5xl text-ink mt-3 max-w-3xl leading-tight">
            Three documents, executed in the only sequence that protects the broker.
          </h2>

          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            {[
              {
                tier: "Tier 1",
                doc: "NCNDA",
                full: "Non-Circumvention, Non-Disclosure Agreement",
                when: "Absolute first step — before any supplier identity, origin or pricing is revealed.",
                does: "Legally binds the buyer, the seller and any co-brokers. They cannot transact or even communicate directly without our written consent for 3 to 5 years. If they try to bypass us, they are liable for full projected commissions plus damages.",
                tone: "border-oxblood",
              },
              {
                tier: "Tier 2",
                doc: "IMFPA",
                full: "Irrevocable Master Fee Protection Agreement",
                when: "Concurrent with — or immediately after — the NCNDA. Always before the SPA.",
                does: "This is the paycheck. It is lodged directly with the buyer and seller's transaction banks and specifies our fee in cents per pound (sugar / coffee) or dollars per barrel (oil). When the buyer's LC clears, the banks automatically split our commission off the top before the seller receives their payout.",
                tone: "border-brass",
              },
              {
                tier: "Tier 3",
                doc: "SPA + LC",
                full: "Sales & Purchase Agreement with Letter of Credit",
                when: "Final contract — only released once the NCNDA and IMFPA are executed and bank-lodged.",
                does: "The buyer and seller execute the SPA. We are named on it as Facilitator / Intermediary. The contract states that the transaction is valid only if the bank terms mirror the protections in the IMFPA. Payment runs through a Transferable DLC or SBLC at top-tier global banks.",
                tone: "border-ink",
              },
            ].map((t) => (
              <div key={t.doc} className={`bg-parchment border-l-4 ${t.tone} border-y border-r border-line p-7`} data-testid={`legal-tier-${t.doc.toLowerCase().split(/\s|\+/)[0]}`}>
                <div className="overline">{t.tier}</div>
                <h3 className="h-serif text-3xl text-ink mt-2">{t.doc}</h3>
                <div className="text-xs font-mono text-sub uppercase tracking-wider mt-1">{t.full}</div>
                <div className="overline mt-5">When it&apos;s signed</div>
                <p className="text-sub text-sm mt-1 leading-relaxed">{t.when}</p>
                <div className="overline mt-5">What it does</div>
                <p className="text-sub text-sm mt-1 leading-relaxed">{t.does}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMISSION SCHEDULE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="commission-schedule">
        <div className="overline">Commission schedule — published openly</div>
        <h2 className="h-serif text-4xl md:text-5xl text-ink mt-3 max-w-3xl leading-tight">
          What we charge. No hidden math.
        </h2>
        <p className="text-sub mt-6 max-w-3xl leading-relaxed">
          Brokerage commission is paid at settlement, lodged through the IMFPA, and split off the top of the
          Buyer&apos;s Letter of Credit before the Seller receives their net payout. The rate card below
          governs <em>standard trial volumes</em> — the typical first lift between a new buyer and seller.
          Final rates are confirmed per-deal at the NCNDA stage and reflect tenor, volume, origin and
          counterparty profile.
        </p>

        {/* Energy */}
        <div className="mt-14">
          <div className="grid md:grid-cols-12 gap-6 items-baseline">
            <div className="md:col-span-4">
              <div className="overline">Energy commodities</div>
              <h3 className="h-serif text-3xl text-ink mt-2">Hard commodities</h3>
              <p className="text-sub text-sm mt-3 leading-relaxed">
                Ordered from lowest per-ton commission to highest. Energy moves in massive bulk,
                so the per-ton fee is tight — total volume compensates for the thin margin.
              </p>
            </div>
            <div className="md:col-span-8 border border-line bg-parchment">
              <table className="w-full text-sm tabular" data-testid="commission-table-energy">
                <thead>
                  <tr className="border-b border-line text-left font-mono text-[11px] text-sub uppercase tracking-wider">
                    <th className="p-4">Commodity</th>
                    <th className="p-4">Standard trial volume</th>
                    <th className="p-4 text-right">Min commission</th>
                    <th className="p-4 text-right">Max commission</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { c: "Crude Oil — WTI / Brent", v: "136,000 MT", min: "$0.07 / MT", max: "$0.37 / MT" },
                    { c: "Refined Oil — EN 590 / Jet A-1", v: "50,000 MT", min: "$1.00 / MT", max: "$2.50 / MT" },
                  ].map((r) => (
                    <tr key={r.c} className="border-b border-line last:border-0">
                      <td className="p-4 h-serif text-lg text-ink">{r.c}</td>
                      <td className="p-4 text-sub">{r.v}</td>
                      <td className="p-4 text-right font-mono text-ink">{r.min}</td>
                      <td className="p-4 text-right font-mono text-oxblood">{r.max}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="border-t border-line bg-parchment2 p-4">
                <div className="overline">Context — Crude</div>
                <p className="text-sub text-xs mt-2 leading-relaxed">
                  The $0.07–$0.37 / MT range translates to the industry-standard $0.01–$0.05 per barrel.
                  A 136,000 MT trial lift is equivalent to roughly <span className="text-ink font-mono">1,000,000 barrels</span>.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Soft */}
        <div className="mt-16">
          <div className="grid md:grid-cols-12 gap-6 items-baseline">
            <div className="md:col-span-4">
              <div className="overline">Soft commodities</div>
              <h3 className="h-serif text-3xl text-ink mt-2">Agricultural origin</h3>
              <p className="text-sub text-sm mt-3 leading-relaxed">
                Higher per-ton margins than energy — softs require complex agricultural
                inspection, grading and provenance documentation, and ship in smaller cargo sizes.
              </p>
            </div>
            <div className="md:col-span-8 border border-line bg-parchment">
              <table className="w-full text-sm tabular" data-testid="commission-table-soft">
                <thead>
                  <tr className="border-b border-line text-left font-mono text-[11px] text-sub uppercase tracking-wider">
                    <th className="p-4">Commodity</th>
                    <th className="p-4">Standard trial volume</th>
                    <th className="p-4 text-right">Min commission</th>
                    <th className="p-4 text-right">Max commission</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { c: "Sugar — ICUMSA 45", v: "12,500 MT", min: "$1.50 / MT", max: "$3.00 / MT" },
                    { c: "Coffee — Arabica / Robusta", v: "21 MT", min: "$5.00 / MT", max: "$10.00 / MT" },
                  ].map((r) => (
                    <tr key={r.c} className="border-b border-line last:border-0">
                      <td className="p-4 h-serif text-lg text-ink">{r.c}</td>
                      <td className="p-4 text-sub">{r.v}</td>
                      <td className="p-4 text-right font-mono text-ink">{r.min}</td>
                      <td className="p-4 text-right font-mono text-oxblood">{r.max}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <p className="text-xs font-mono text-sub uppercase tracking-wider mt-10 max-w-3xl">
          Indicative ranges. Final commission is lodged in the IMFPA for each transaction
          and is irrevocable once executed.
        </p>
      </section>

      {/* CTA */}
      <section className="bg-ink text-parchment grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
          <div>
            <div className="overline !text-brass">Bring a real deal</div>
            <h2 className="h-serif text-4xl md:text-5xl mt-3 max-w-3xl leading-tight">
              Have an ICPO and a Bank Comfort Letter ready? Open an account and submit.
            </h2>
          </div>
          <Link to="/login" className="btn-brass" data-testid="process-request-access">
            Submit an ICPO →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
